import { getFirebase, globalApi, listenFirebase, setFirebase, updateFirebase } from 'modules/App/globalApi'
import { getInitCheckoutPage, getInitSalesPage } from 'modules/NewCoachOrgQuestionnaire/templates/initDataTemplates'

const SALES_PAGE_REMOVABLE_FIELDS = ['testimonials', 'faqs', 'images']
const CHECKOUT_PAGE_REMOVABLE_FIELDS = ['benefits', 'heroImage']

const withEmptiedFieldsCleared = (draft, removableFields) =>
  removableFields.reduce((page, field) => ({ ...page, [field]: draft?.[field] || null }), { ...draft })

export const pageBuilderApi = globalApi.injectEndpoints({
  endpoints: (build) => ({
    listenSalesPage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage`
          : `drafts/${coachOrgId}/salesPage`
        listenFirebase(path, api, async (page) => {
          if (!page && coachOrgId) {
            const { data: primaryColor } = await getFirebase(
              `coachOrgs/${coachOrgId}/styles/colorScheme/primaryUIColor`
            )
            const { data: coachName } = await getFirebase(`/coachOrgs/${coachOrgId}/coachName`)
            const draftPage = getInitSalesPage({ primaryColor, coachName })
            await setFirebase(path, draftPage)
            api.updateCachedData(() => draftPage)
          } else {
            api.updateCachedData(() => page)
          }
        })
      },
    }),
    listenCheckoutPage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/siteCheckout`
          : `drafts/${coachOrgId}/siteCheckout`
        listenFirebase(path, api, async (page) => {
          if (!page && coachOrgId) {
            const draftPage = getInitCheckoutPage({ isOneTimeProduct: Boolean(productId) })
            await setFirebase(path, draftPage)
            api.updateCachedData(() => draftPage)
          } else {
            api.updateCachedData(() => page)
          }
        })
      },
    }),
    listenPublishedSalesPage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}/salesPage`
          : `coachOrgs/${coachOrgId}/productInfo/salesPage`
        listenFirebase(path, api)
      },
    }),
    listenPublishedCheckoutPage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}/siteCheckout`
          : `coachOrgs/${coachOrgId}/productInfo/siteCheckout`
        listenFirebase(path, api)
      },
    }),
    listenDraftPrimaryColor: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/primaryColor`
          : `drafts/${coachOrgId}/salesPage/primaryColor`
        listenFirebase(path, api)
      },
    }),
    listenAllOrgSlugs: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async (_, api) => {
        listenFirebase(`internal/coachOrgSlugs`, api)
      },
    }),
    listenCoachOrgSlug: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        listenFirebase(`internal/coachOrgSlugs/${coachOrgId}`, api)
      },
    }),
    setCoachOrgSlug: build.mutation({
      queryFn: async ({ coachOrgId, slug }) => {
        const { data, error } = await setFirebase(`internal/coachOrgSlugs/${coachOrgId}`, slug)
        return { data, error }
      },
    }),
    setDraftPrimaryColor: build.mutation({
      queryFn: async ({ coachOrgId, color, productId }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/primaryColor`
          : `drafts/${coachOrgId}/salesPage/primaryColor`
        const { data, error } = await setFirebase(path, color)
        return { data, error }
      },
    }),
    publishSalesPage: build.mutation({
      queryFn: async ({ coachOrgId, productId }) => {
        const draftPath = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage`
          : `drafts/${coachOrgId}/salesPage`
        const publishPath = productId
          ? `coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}/salesPage`
          : `coachOrgs/${coachOrgId}/productInfo/salesPage`

        const { data: salesPageDraft, error: salesPageDraftError } = await getFirebase(draftPath)
        if (salesPageDraftError) {
          return { error: salesPageDraftError }
        }

        const { data, error: publishSalesPageError } = await updateFirebase(
          publishPath,
          withEmptiedFieldsCleared(salesPageDraft, SALES_PAGE_REMOVABLE_FIELDS)
        )
        if (publishSalesPageError) {
          return { error: publishSalesPageError }
        }

        return { data }
      },
    }),
    publishCheckoutPage: build.mutation({
      queryFn: async ({ coachOrgId, productId }) => {
        const draftPath = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/siteCheckout`
          : `drafts/${coachOrgId}/siteCheckout`
        const publishPath = productId
          ? `coachOrgs/${coachOrgId}/productInfo/oneTimeProducts/${productId}/siteCheckout`
          : `coachOrgs/${coachOrgId}/productInfo/siteCheckout`

        const { data: checkoutPageDraft, error: fetchCheckoutPageDraftError } = await getFirebase(draftPath)
        if (fetchCheckoutPageDraftError) {
          return { error: fetchCheckoutPageDraftError }
        }

        const { data, error: publishCheckoutPageError } = await updateFirebase(
          publishPath,
          withEmptiedFieldsCleared(checkoutPageDraft, CHECKOUT_PAGE_REMOVABLE_FIELDS)
        )
        if (publishCheckoutPageError) {
          return { error: publishCheckoutPageError }
        }

        return { data }
      },
    }),
    listenSalesHeroImage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images/hero`
          : `drafts/${coachOrgId}/salesPage/images/hero`
        listenFirebase(path, api)
      },
    }),
    listenSalesImages: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images`
          : `drafts/${coachOrgId}/salesPage/images`
        listenFirebase(path, api)
      },
    }),
    listenSalesHighlightsImage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images/trifold`
          : `drafts/${coachOrgId}/salesPage/images/trifold`
        listenFirebase(path, api)
      },
    }),
    listenSalesAboutImage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images/single`
          : `drafts/${coachOrgId}/salesPage/images/single`
        listenFirebase(path, api)
      },
    }),
    updateSalesHeroSection: build.mutation({
      queryFn: async ({ coachOrgId, productId, section }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/heroSection`
          : `drafts/${coachOrgId}/salesPage/heroSection`
        const { data, error } = await setFirebase(path, section)
        return { data, error }
      },
    }),
    updateSalesHeroImage: build.mutation({
      queryFn: async ({ coachOrgId, productId, image }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images/hero`
          : `drafts/${coachOrgId}/salesPage/images/hero`
        const { data, error } = await setFirebase(path, image)
        return { data, error }
      },
    }),
    updateSalesHeroImgDeskPos: build.mutation({
      queryFn: async ({ coachOrgId, productId, position }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images/heroImgDesktopPosition`
          : `drafts/${coachOrgId}/salesPage/images/heroImgDesktopPosition`
        const { data, error } = await setFirebase(path, position)
        return { data, error }
      },
    }),
    updateSalesHeroImgMobPos: build.mutation({
      queryFn: async ({ coachOrgId, productId, position }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images/heroImgMobilePosition`
          : `drafts/${coachOrgId}/salesPage/images/heroImgMobilePosition`
        const { data, error } = await setFirebase(path, position)
        return { data, error }
      },
    }),
    updateSalesHightlightsSection: build.mutation({
      queryFn: async ({ coachOrgId, productId, section }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/programSummary`
          : `drafts/${coachOrgId}/salesPage/programSummary`
        const { data, error } = await updateFirebase(path, section)
        return { data, error }
      },
    }),
    updateSalesBannerSection: build.mutation({
      queryFn: async ({ coachOrgId, productId, section }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/banner`
          : `drafts/${coachOrgId}/salesPage/banner`
        const { data, error } = await setFirebase(path, section)
        return { data, error }
      },
    }),
    updateSalesAboutSection: build.mutation({
      queryFn: async ({ coachOrgId, productId, section }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/aboutMe`
          : `drafts/${coachOrgId}/salesPage/aboutMe`
        const { data, error } = await setFirebase(path, section)
        return { data, error }
      },
    }),
    updateCheckoutHero: build.mutation({
      queryFn: async ({ coachOrgId, productId, image }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/siteCheckout/heroImage`
          : `drafts/${coachOrgId}/siteCheckout/heroImage`
        const { data, error } = await setFirebase(path, image)
        return { data, error }
      },
    }),
    updateCheckoutBenefits: build.mutation({
      queryFn: async ({ coachOrgId, productId, benefits }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/siteCheckout/benefits`
          : `drafts/${coachOrgId}/siteCheckout/benefits`
        const { data, error } = await setFirebase(path, benefits)
        return { data, error }
      },
    }),
    updateCheckoutHeroMainText: build.mutation({
      queryFn: async ({ coachOrgId, productId, text }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/siteCheckout/heroSection/mainText`
          : `drafts/${coachOrgId}/siteCheckout/heroSection/mainText`
        const { data, error } = await setFirebase(path, text)
        return { data, error }
      },
    }),
    updateSalesFaqSection: build.mutation({
      queryFn: async ({ coachOrgId, productId, faqs }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/faqs`
          : `drafts/${coachOrgId}/salesPage/faqs`
        const { data, error } = await setFirebase(path, faqs)
        return { data, error }
      },
    }),
    listenTestimonialImage: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId, id }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/images/testimonials/${id}`
          : `drafts/${coachOrgId}/salesPage/images/testimonials/${id}`
        listenFirebase(path, api)
      },
    }),
    updateSalesTestimonialSection: build.mutation({
      queryFn: async ({ coachOrgId, productId, testimonials }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/testimonials`
          : `drafts/${coachOrgId}/salesPage/testimonials`
        const { data, error } = await setFirebase(path, testimonials)
        return { data, error }
      },
    }),
    listenSalesPageSeo: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/seo`
          : `drafts/${coachOrgId}/salesPage/seo`
        listenFirebase(path, api)
      },
    }),
    updateSalesPageSeo: build.mutation({
      queryFn: async ({ coachOrgId, productId, seo }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/salesPage/seo`
          : `drafts/${coachOrgId}/salesPage/seo`
        const { data, error } = await setFirebase(path, seo)
        return { data, error }
      },
    }),
    listenCheckoutPageSeo: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, productId }, api) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/siteCheckout/seo`
          : `drafts/${coachOrgId}/siteCheckout/seo`
        listenFirebase(path, api)
      },
    }),
    updateCheckoutPageSeo: build.mutation({
      queryFn: async ({ coachOrgId, productId, seo }) => {
        const path = productId
          ? `drafts/${coachOrgId}/oneTimeProducts/${productId}/siteCheckout/seo`
          : `drafts/${coachOrgId}/siteCheckout/seo`
        const { data, error } = await setFirebase(path, seo)
        return { data, error }
      },
    }),
    listenSeoImg: build.query({
      queryFn: () => ({ data: { isLoading: true } }),
      onCacheEntryAdded: async ({ coachOrgId, uploadLocation }, api) => {
        listenFirebase(`drafts/${coachOrgId}/${uploadLocation}/seo/previewImg`, api)
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useListenSalesPageQuery,
  useListenCheckoutPageQuery,
  useListenPublishedSalesPageQuery,
  useListenPublishedCheckoutPageQuery,
  useListenDraftPrimaryColorQuery,
  useListenCoachOrgSlugQuery,
  useListenAllOrgSlugsQuery,
  useSetCoachOrgSlugMutation,
  useSetDraftPrimaryColorMutation,
  usePublishSalesPageMutation,
  usePublishCheckoutPageMutation,
  useListenSalesHeroImageQuery,
  useListenSalesImagesQuery,
  useListenSalesHighlightsImageQuery,
  useListenSalesAboutImageQuery,
  useUpdateSalesHeroSectionMutation,
  useUpdateSalesHeroImageMutation,
  useUpdateSalesHeroImgDeskPosMutation,
  useUpdateSalesHeroImgMobPosMutation,
  useUpdateSalesHightlightsSectionMutation,
  useUpdateSalesBannerSectionMutation,
  useUpdateSalesAboutSectionMutation,
  useUpdateCheckoutHeroMutation,
  useUpdateCheckoutBenefitsMutation,
  useUpdateCheckoutHeroMainTextMutation,
  useUpdateSalesFaqSectionMutation,
  useListenTestimonialImageQuery,
  useUpdateSalesTestimonialSectionMutation,
  useListenSalesPageSeoQuery,
  useUpdateSalesPageSeoMutation,
  useListenCheckoutPageSeoQuery,
  useUpdateCheckoutPageSeoMutation,
  useListenSeoImgQuery,
} = pageBuilderApi
